import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CtWorkspaceDto,
  LayerItemDto,
  LayerItemSettingsDto,
  LayerThematicStylesDto,
  WorkspaceConnectSettingDto,
  WorkspaceMapSettingsDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { MasterOperatorFields } from '@cloud-twin/master-operator/models/master-operator-fields.enum';
import { SetValueAction } from '@common-modules/dynamic-layout/state/generic/generic.actions';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { AuthenticationService } from '@common-modules/shared/auth/services/authentication.service';
import { BaseService } from '@common-modules/shared/base.service';
import { ArrayHelperService } from '@common-modules/shared/helpers/array-helper.service';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { GlobalsService } from '@common-modules/shared/services/globals.service';
import { LayerItemV2 } from '@common-modules/wlm-map/generic-map/models/layer-item';
import { ThematicMapService } from '@common-modules/wlm-map/generic-map/services/thematic-map.service';
import { combineLatest, map, Observable, ReplaySubject, Subscription, take } from 'rxjs';
import { CTMapFiltrableProperties } from '../models/ct-map-filtrable-properties';

@Injectable({ providedIn: 'root' })
export class CTMapPersistencyService extends BaseService {
  private _settings$ = new ReplaySubject<WorkspaceMapSettingsDto>(1);
  readonly settings$ = this._settings$.asObservable();
  private _saveSettings$ = new ReplaySubject<WorkspaceMapSettingsDto>(1);

  private _settings: WorkspaceMapSettingsDto;
  private _saveSettingsSubs: Subscription;
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _arrayHelperService = inject(ArrayHelperService);
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _state = inject(ReduxStateService);
  private readonly _thematicMapService = inject(ThematicMapService);
  private readonly _globalsService = inject(GlobalsService);

  private _canPersist = false;

  private get entityUrl(): string {
    return `${this.apiUrl}/workspace-map-settings`;
  }

  private initSettings(settings: WorkspaceMapSettingsDto): void {
    this._settings = settings;
    this._settings$.next(settings);
  }

  private saveSettings(settings: WorkspaceMapSettingsDto): void {
    this._settings = settings;
    this._settings$.next(settings);
    this._saveSettings$.next(settings);
  }

  init(workspace: CtWorkspaceDto): void {
    if (!workspace) {
      this.initSettings(null);
      return;
    }

    this.isSelectedWorkspaceOwnerOrSuperUser$(workspace).subscribe(
      (isSelectedWorkspaceOwnerOrSuperUser) => {
        this._canPersist = isSelectedWorkspaceOwnerOrSuperUser;

        this.load(workspace.workspaceId).subscribe((settings) => this.initSettings(settings));

        // Save any change
        this._saveSettingsSubs?.unsubscribe();
        this._saveSettingsSubs = this._saveSettings$
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe((settings) => {
            this.save(settings);
          });
      }
    );
  }

  createInitialPersistency(workspace: CtWorkspaceDto, layerItems: LayerItemV2[]): void {
    const persistencyLayerItems: LayerItemDto[] = layerItems.map(
      (layerItem, index) =>
        new LayerItemDto({
          key: layerItem.layerKey,
          settings: new LayerItemSettingsDto({
            isVisible: layerItem.isVisible,
            styleId: layerItem.layerStyle?.id,
            order: index,
          }),
        })
    );

    const visibleScenarioIds = this.getAllScenarioIds(workspace.workspaceConnectSettings);

    const settings = new WorkspaceMapSettingsDto({
      id: globalUtilsHelper.generateGuid(),
      workspaceId: workspace.workspaceId,
      visibleScenarioIds,
      layerItems: persistencyLayerItems,
      thematicLayerItems: [],
    });

    this.saveSettings(settings);
  }

  /**
   * From API to front
   */

  applyLayerItemPersistency(
    layerItems: LayerItemV2[],
    persistencySettings: WorkspaceMapSettingsDto,
    currentThematics: LayerThematicStylesDto[]
  ): LayerItemV2[] {
    let updatedLayerItems = this.addThematicLayers(
      layerItems,
      persistencySettings,
      currentThematics
    );
    updatedLayerItems = this.applySorting(updatedLayerItems, persistencySettings);
    updatedLayerItems = this.applyVisibilityAndStyle(updatedLayerItems, persistencySettings);

    return updatedLayerItems;
  }

  private addThematicLayers(
    layerItems: LayerItemV2[],
    persistencySettings: WorkspaceMapSettingsDto,
    currentThematics: LayerThematicStylesDto[]
  ): LayerItemV2[] {
    const thematicLayerItems: LayerItemV2[] = [];

    for (const thematicLayerItem of persistencySettings.thematicLayerItems) {
      const thematic = currentThematics.find(
        (thematic) => thematic.id === thematicLayerItem.settings.styleId
      );
      if (thematic) {
        const baseLayerItem = layerItems.find((item) => {
          const filtrable =
            (item as any).currentFiltrableProperties || (item as any).filtrableProperties;
          return (filtrable as CTMapFiltrableProperties)?.neType === thematic.objectType;
        });
        if (baseLayerItem) {
          const thematicLayerItem = this._thematicMapService.buildThematicLayerItem(
            baseLayerItem,
            thematic,
            layerItems
          );
          thematicLayerItems.push(thematicLayerItem);
        }
      }
    }

    const allLayerItems = thematicLayerItems.concat(layerItems);
    return allLayerItems;
  }

  private applySorting(
    layerItems: LayerItemV2[],
    persistencySettings: WorkspaceMapSettingsDto
  ): LayerItemV2[] {
    const allPersistedLayers = persistencySettings.thematicLayerItems.concat(
      persistencySettings.layerItems
    );
    const sortByReferenceOrder = globalUtilsHelper.sortByReferenceOrder;
    const sortObjects = this._arrayHelperService.sortObjects;

    const sortedPersistedLayers = sortObjects(allPersistedLayers, (item) => item.settings.order);
    const sortedLayerItems: LayerItemV2[] = sortByReferenceOrder(
      sortedPersistedLayers,
      layerItems,
      (item) => item.layerKey ?? item.key
    );

    // Keep the paneIndex integrity
    sortedLayerItems.forEach((layerItem, index) => {
      layerItem.paneIndex = sortedLayerItems.length - index;
    });

    return sortedLayerItems;
  }

  private applyVisibilityAndStyle(
    layerItems: LayerItemV2[],
    persistencySettings: WorkspaceMapSettingsDto
  ): LayerItemV2[] {
    const allPersistedLayers = persistencySettings.thematicLayerItems.concat(
      persistencySettings.layerItems
    );
    const layersVisibility: { [type: string]: boolean } = {};
    layerItems.forEach((layerItem) => {
      const persistedLayer = allPersistedLayers.find(
        (persistedItem) => persistedItem.key === layerItem.layerKey
      );
      if (persistedLayer) {
        layerItem.isVisible = persistedLayer.settings.isVisible;
        if (!layerItem.layerDefinition.isThematic) {
          layersVisibility[persistedLayer.key] = persistedLayer.settings.isVisible;
        }
        // Thematics were already handled
        if (!layerItem.layerDefinition.isThematic) {
          const layerStyle = layerItem.availableStyles.find(
            (style) => style.id === persistedLayer.settings.styleId
          );
          layerItem.layerStyle = layerStyle;
        }
      }
    });

    this.dispatchLayersVisibility(layersVisibility);

    return layerItems;
  }

  private dispatchLayersVisibility(layers: { [type: string]: boolean }) {
    this._state.dispatch(
      new SetValueAction({
        fieldName: MasterOperatorFields.LayersVisibility,
        value: layers,
      })
    );
  }

  private getAllScenarioIds = (connectSettings: WorkspaceConnectSettingDto[]) => {
    const scenarioIds = new Set<string>();
    connectSettings.forEach((item) => scenarioIds.add(item.scenarioId));
    return Array.from(scenarioIds.values());
  };

  /**
   * From front to API
   */

  persistScenarioVisibility(scenarioId: string, isVisible: boolean): void {
    if (!this._canPersist) {
      return;
    }

    const settings = this._settings;
    let visibleScenarioIds = settings.visibleScenarioIds ?? [];

    if (
      isVisible &&
      !visibleScenarioIds.find((visibleScenarioId) => visibleScenarioId === scenarioId)
    ) {
      visibleScenarioIds.push(scenarioId);
    }

    if (!isVisible) {
      visibleScenarioIds = visibleScenarioIds.filter(
        (visibleScenarioId) => visibleScenarioId !== scenarioId
      );
    }

    settings.visibleScenarioIds = visibleScenarioIds;
    this.saveSettings(settings);
  }

  // Works for both topology and thematic layers.
  persistLayerVisibility(layerItem: LayerItemV2, isVisible: boolean): void {
    if (!this._canPersist) {
      return;
    }

    this.updateLayerItem(layerItem, (persistedItem) => {
      persistedItem.settings.isVisible = isVisible;
    });
  }

  persistThematicCreated(layerItem: LayerItemV2): void {
    if (!this._canPersist) {
      return;
    }

    const settings = this._settings;

    const persistedLayer = new LayerItemDto({
      key: layerItem.layerKey,
      settings: new LayerItemSettingsDto({
        isVisible: true, // Visible by default
        styleId: layerItem.layerStyle.id,
        order: 0, // Always created on top by default
      }),
    });

    // Add to top
    settings.thematicLayerItems.unshift(persistedLayer);
    this.updateOrderByPositions();

    this.saveSettings(settings);
  }

  persistThematicDeleted(layerItem: LayerItemV2): void {
    if (!this._canPersist) {
      return;
    }

    const settings = this._settings;
    settings.thematicLayerItems = settings.thematicLayerItems.filter(
      (item) => item.key !== layerItem.layerKey
    );
    this.updateOrderByPositions();

    this.saveSettings(settings);
  }

  persistLayersOrder(layerItems: LayerItemV2[]): void {
    if (!this._canPersist) {
      return;
    }

    const sortByReferenceOrder = globalUtilsHelper.sortByReferenceOrder;
    const identityFn = (item) => item.layerKey ?? item.key;

    const thematicLayers = layerItems.filter((layerItem) => layerItem.layerDefinition.isThematic);
    const topologyLayers = layerItems.filter((layerItem) => !layerItem.layerDefinition.isThematic);

    const settings = this._settings;
    settings.thematicLayerItems = sortByReferenceOrder(
      thematicLayers,
      settings.thematicLayerItems,
      identityFn
    );
    settings.layerItems = sortByReferenceOrder(topologyLayers, settings.layerItems, identityFn);
    this.updateOrderByPositions();

    this.saveSettings(settings);
  }

  private updateOrderByPositions(): void {
    const settings = this._settings;
    settings.thematicLayerItems.forEach((item, index) => (item.settings.order = index));
    const totalThematics = settings.thematicLayerItems.length;
    // Topology layers always come after thematics.
    settings.layerItems.forEach((item, index) => (item.settings.order = totalThematics + index));
  }

  private updateLayerItem(
    layerItem: LayerItemV2,
    applyChange: (persistedItem: LayerItemDto) => any
  ) {
    const settings = this._settings;
    const isThematic = layerItem.layerDefinition.isThematic;

    const items = isThematic ? settings.thematicLayerItems : settings.layerItems;

    items.forEach((persistedItem) => {
      if (persistedItem.key === layerItem.layerKey) {
        applyChange(persistedItem);
      }
    });

    if (isThematic) {
      settings.thematicLayerItems = items;
    } else {
      settings.layerItems = items;
    }

    this.saveSettings(settings);
  }

  private load(workspaceId: string): Observable<WorkspaceMapSettingsDto> {
    return this.httpCacheClient.get<WorkspaceMapSettingsDto>(`${this.entityUrl}/${workspaceId}`, {
      avoid: true,
    });
  }

  private save(settings: WorkspaceMapSettingsDto): void {
    if (!settings) {
      return;
    }

    this.httpClient.post<string>(this.entityUrl, settings).subscribe((id) => {
      this._settings.id = id;
    });
  }

  private isSelectedWorkspaceOwnerOrSuperUser$(workspace: CtWorkspaceDto): Observable<boolean> {
    return combineLatest([
      this._authenticationService.userCode$,
      this._globalsService.getCurrentUser(),
    ]).pipe(
      take(1),
      map(([userCode, currentUser]) => currentUser.isSuperUser || workspace.ownerCode === userCode)
    );
  }
}
